import { api } from "../base";

export interface BlotterHistoryParams {
  accountId: string;
  feedType: string;
  direction?: "asc" | "desc";
  limit?: number;
  startMs?: string | null;
  endMs?: string | null;
  paginationToken?: string | null;
}

export interface BlotterHistoryPage {
  data: unknown[];
  token?: string;
}

const blotterEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Fetches a page of dropcopy history for a single account and feed type.
     * Supports pagination via token and date filtering via startMs.
     *
     * Note: blotter queries are NOT cached aggressively — each page request
     * with different params is a unique cache entry. Use `keepUnusedDataFor: 0`
     * if memory is a concern.
     */
    getBlotterHistory: build.query<BlotterHistoryPage, BlotterHistoryParams>({
      query: ({
        accountId,
        feedType,
        direction = "desc",
        limit = 500,
        startMs,
        endMs,
        paginationToken,
      }) => {
        const params: Record<string, string> = {};
        if (paginationToken) {
          params.token = paginationToken;
        } else {
          params.limit = String(limit);
          params.direction = direction;
          if (startMs) params.start_ms = startMs;
          if (endMs) params.end_ms = endMs;
        }
        return {
          url: `/api/dropcopy/v1/history/${accountId}/${feedType}`,
          params,
        };
      },
      providesTags: (_result, _error, { accountId, feedType }) => [
        { type: "BlotterHistory", id: `${accountId}:${feedType}` },
      ],
    }),
  }),
});

export const { useGetBlotterHistoryQuery, useLazyGetBlotterHistoryQuery } =
  blotterEndpoints;
